import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { validateIntakeText } from "../../utils/intakeTextValidation";
import {
  itemSourceBadge,
  isSectionRequiredForIntake, formatFactQuestionText,
  isGate0FrozenItem,
  isStaleUnlinkedCapture,
  countSectionCaptured,
} from "../../utils/chatUtils";
import { ReactComponent as EditIcon } from "../../asserts/logo/edit_pencil.svg";
import { ReactComponent as TickMarkIcon } from "../../asserts/logo/tick_mark.svg";
import { ReactComponent as IntoMarkIcon } from "../../asserts/logo/into_mark.svg";

const SOURCE_COLORS = {
  "Gate 0": "#197F5C",
  Doc: "#0046B8",
  Chat: "#3895FF",
  Pending: "#707070",
  // Inferred: "#9551EC",
};

/** Shared grid columns: [chevron | content | actions] + horizontal pad */
const SECTION_PAD_X = 1.5;
const CHEVRON_WIDTH = 18;
const HEADER_GAP = 0.75;

const iconCellSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  minHeight: 18,
};

function sectionRowGrid(extra = {}) {
  return {
    display: "grid",
    gridTemplateColumns: `${CHEVRON_WIDTH}px 1fr 80px`,
    columnGap: "4px",
    px: SECTION_PAD_X,
    boxSizing: "border-box",
    ...extra,
  };
}

const factRowTextSx = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#383838",
  minHeight: 24,
};

const factRowLabelSx = {
  ...factRowTextSx,
  fontWeight: 400,
};

const factRowValueSx = {
  ...factRowTextSx,
  fontWeight: 700,
};

/** One tooltip line: the "Q :" / "A :" label keeps its own column so wrapped text stays aligned with the first character after the colon. */
function FactTooltipLine({ label, text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box component="span" sx={{ flexShrink: 0, whiteSpace: "nowrap", mr: "4px" }}>
        {label} :
      </Box>
      <Box
        component="span"
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          whiteSpace: "pre-line",
          overflowWrap: "anywhere",
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

/** Single line: full question + answer on the right; hover shows unified Q: / A: tooltip. */
function FactQuestionAnswer({ question, value, pending = false }) {
  const displayValue = value != null ? String(value).trim() : "";
  const hasValue = displayValue.length > 0;

  const tooltipContent = (
    <>
      <FactTooltipLine label="Q" text={question} />
      {hasValue && <FactTooltipLine label="A" text={displayValue} />}
    </>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="bottom"
      enterDelay={200}
      componentsProps={factValueTooltipSlotProps()}
    >
      <Box
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          cursor: "default",
          outline: "none",
        }}
      >
        <Box
          component="span"
          sx={{
            ...factRowLabelSx,
            flexShrink: 0,
            whiteSpace: "nowrap",
            ...(pending ? { color: INTAKE_COLORS.textMuted } : {}),
          }}
        >
          {formatFactQuestionText(question)}
        </Box>
        {!pending && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: "1 1 0",
              minWidth: 0,
              overflow: "hidden",
              pl: 0.25,
              justifyContent: "flex-start",
            }}
          >
            <Box component="span" sx={{ ...factRowLabelSx, flexShrink: 0, px: 0.25 }}>:</Box>
            <Box
              component="span"
              sx={{
                ...factRowValueSx,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              {displayValue}
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function factValueTooltipSlotProps() {
  return {
    tooltip: {
      sx: {
        maxWidth: 300,
        bgcolor: "#000000",
        borderRadius: "4px",
        py: "8px",
        px: "12px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "18px",
        letterSpacing: 0,
        color: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
        "& .MuiTooltip-arrow": { color: "#000000" },
      },
    },
  };
}

function getSectionStatus(cap, total) {
  if (total > 0 && cap >= total) return "complete";
  if (cap > 0) return "inProgress";
  return "notStarted";
}

/*function SourceChip({ label }) {
  const styles = {
    "Gate 0": { bg: INTAKE_COLORS.successBadgeBg, color: SOURCE_COLORS["Gate 0"] },
    Doc: { bg: INTAKE_COLORS.primary20, color: SOURCE_COLORS.Doc },
    Chat: { bg: "#E8F4FF", color: SOURCE_COLORS.Chat },
    Pending: { bg: INTAKE_COLORS.g1, color: SOURCE_COLORS.Pending },
    // Inferred: { bg: INTAKE_COLORS.purpleLight, color: SOURCE_COLORS.Inferred },
  };
  const chip = styles[label] || styles.Pending;
  return (
    <Box
      sx={{
        px: 3,
        py: 0.5,
        borderRadius: "12px",
        bgcolor: chip.bg,
        color: chip.color,
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        lineHeight: 1.4,
      }}
    >
      {label}
    </Box>
  );
}*/

function SectionStatusBadge({ cap, total }) {
  const status = getSectionStatus(cap, total);
  const color =
    status === "complete" ? "#383838" : status === "inProgress" ? INTAKE_COLORS.warning60 : INTAKE_COLORS.grey60;

  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: "20px",
        letterSpacing: 0,
        color,
        textAlign: "right",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {cap}/{total}
    </Typography>
  );
}

function EditFactButton({ onClick, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Edit fact"
      sx={{
        border: "none",
        bgcolor: "transparent",
        p: 0.25,
        m: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        flexShrink: 0,
        "&:hover": disabled ? {} : { opacity: 0.75 },
      }}
    >
      <EditIcon style={{ width: 18, height: 18, display: "block" }} />
    </Box>
  );
}

function IconActionButton({ onClick, disabled, title, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      sx={{
        border: "none",
        bgcolor: "transparent",
        p: 0.5,
        m: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

function SubItemRow({
  item,
  capd,
  badge,
  dotColor,
  rowBg,
  isEditing,
  editDraft,
  editError,
  editDisabled,
  gate0Locked = false,
  onStartEdit,
  onEditDraftChange,
  onSaveEdit,
  onCancelEdit,
}) {
  if (isEditing && !gate0Locked) {
    return (
      <Box
        sx={sectionRowGrid({
          alignItems: "flex-start",
          py: 1.25,
          bgcolor: rowBg,
        })}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={iconCellSx}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: INTAKE_COLORS.primary, flexShrink: 0 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...factRowLabelSx, color: INTAKE_COLORS.textMuted, mb: 0.75 }}>
            {formatFactQuestionText(item.text)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="input"
                value={editDraft}
                onChange={(e) => onEditDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSaveEdit();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    onCancelEdit();
                  }
                }}
                autoFocus
                sx={{
                  width: "100%",
                  border: `1px solid ${INTAKE_COLORS.text}`,
                  borderRadius: "4px",
                  px: 1.25,
                  py: 0.875,
                  fontSize: 13,
                  fontWeight: 500,
                  color: INTAKE_COLORS.text,
                  fontFamily: INTAKE_FONT,
                  outline: "none",
                  boxSizing: "border-box",
                  "&:focus": { borderColor: INTAKE_COLORS.primary },
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  color: editError ? INTAKE_COLORS.red : INTAKE_COLORS.g4,
                  mt: 0.625,
                }}
              >
                {editError || `Enter ${item.text.toLowerCase()}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, pt: 0.75 }}>
              <IconActionButton
                onClick={onSaveEdit}
                disabled={!editDraft.trim() || editDisabled}
                title="Save"
              >
                <TickMarkIcon style={{ width: 18, height: 14, display: "block" }} />
              </IconActionButton>
              <IconActionButton onClick={onCancelEdit} title="Cancel">
                <IntoMarkIcon style={{ width: 14, height: 14, display: "block" }} />
              </IconActionButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={sectionRowGrid({
        gridTemplateColumns: `${CHEVRON_WIDTH}px minmax(0, 1fr) 24px`,
        py: 0.75,
        alignItems: "center",
        minHeight: 24,
        bgcolor: INTAKE_COLORS.surface,
        ...(gate0Locked
          ? {}
          : {
              "& .sub-item-edit-btn": { display: "none" },
              "&:hover": { bgcolor: INTAKE_COLORS.g1 },
              "&:hover .sub-item-edit-btn": { display: "flex" },
            }),
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={iconCellSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: INTAKE_COLORS.primary, flexShrink: 0 }} />
      </Box>
      <Box sx={{ minWidth: 0, pr: 0.5 }}>
        <FactQuestionAnswer
          question={item.text}
          value={capd?.value}
          pending={badge === "Pending"}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        
        {!gate0Locked && (
          <Box className="sub-item-edit-btn">
            <EditFactButton onClick={onStartEdit} disabled={editDisabled} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function SectionProgressBar({ cap, total }) {
  const pct = total ? Math.min(100, Math.round((cap / total) * 100)) : 0;
  const isComplete = pct >= 100;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        sx={{
          flex: 1,
          height: 3,
          borderRadius: 1,
          overflow: "hidden",
          minWidth: 0,
          position: "relative",
          ...(isComplete
            ? { bgcolor: INTAKE_COLORS.progressGreen }
            : {
                backgroundImage: `${INTAKE_COLORS.progressTrack}, linear-gradient(${INTAKE_COLORS.g2}, ${INTAKE_COLORS.g2})`,
                backgroundBlendMode: "overlay, normal",
              }),
        }}
      >
        {!isComplete && (
          <Box
            sx={{
              height: "100%",
              width: `${pct}%`,
              background: `linear-gradient(0deg, ${INTAKE_COLORS.progressGreen}, ${INTAKE_COLORS.progressGreen})`,
              borderRadius: 1,
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: INTAKE_COLORS.progressGreen,
          flexShrink: 0,
          minWidth: 32,
          textAlign: "right",
        }}
      >
        {pct}%
      </Typography>
    </Box>
  );
}

export default function IntakeSidebar({
  sections,
  captured,
  gate0Data = {},
  onEditItem,
  onEditSuccess,
  editDisabled = false,
  width = 320,
}) {
  const [openSections, setOpenSections] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [editError, setEditError] = useState("");

  const allOpen = sections?.length > 0 && sections.every((s) => openSections.includes(s.id));
  const requiredCount = (sections || []).filter((section) => isSectionRequiredForIntake(section)).length;
  const optionalCount = Math.max((sections || []).length - requiredCount, 0);

  const toggleSection = (id) => {
    setOpenSections((prev) => (prev.includes(id) ? [] : [id]));
  };

  const expandAll = () => {
    const allIds = (sections || []).map((s) => s.id);
    setOpenSections(allOpen ? [] : allIds);
  };

  const startEdit = (item, capd, sectionId) => {
    if (isGate0FrozenItem(item, gate0Data, captured, sectionId)) return;
    setEditingItemId(item.id);
    setEditDraft(capd?.value ? String(capd.value) : "");
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingItemId(null);
    setEditDraft("");
    setEditError("");
  };

  const saveEdit = async (item) => {
    if (editDisabled) return;
    const validated = validateIntakeText(editDraft);
    if (!validated.ok) {
      setEditError(validated.message);
      return;
    }
    const current = captured?.[item.id]?.value;
    if (current != null && String(current) === validated.value) {
      cancelEdit();
      return;
    }
    const label = item.text;
    try {
      await onEditItem?.(item, validated.value);
      onEditSuccess?.(label);
      cancelEdit();
    } catch (err) {
      setEditError(err?.message || "Could not save this change. Please try again.");
    }
  };

  if (!sections?.length) {
    return (
      <Box
        sx={{
          width,
          flexShrink: 0,
          bgcolor: INTAKE_COLORS.surface,
          borderRight: "none",
          p: 2,
          fontFamily: INTAKE_FONT,
        }}
      >
        <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.textMuted }}>No sections loaded yet.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        bgcolor: INTAKE_COLORS.surface,
        borderRight: "none",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        fontFamily: INTAKE_FONT,
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: SECTION_PAD_X,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: -8,
            bottom: 0,
            borderBottom: `1px solid ${INTAKE_COLORS.border}`,
            pointerEvents: "none",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "18px",
            letterSpacing: 0,
            color: "#383838",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          Required{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {requiredCount}
          </Box>
          {" | Optional "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {optionalCount}
          </Box>
        </Typography>
        {sections?.length > 0 && (
          <Box
            component="button"
            onClick={expandAll}
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: INTAKE_COLORS.primary,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: INTAKE_FONT,
              whiteSpace: "nowrap",
              flexShrink: 0,
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.28) transparent",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderRadius: 999,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.35)",
          },
          "&::-webkit-scrollbar-button": {
            display: "none",
            width: 0,
            height: 0,
          },
        }}
      >
        {sections.map((s) => {
          const secItems = s.items || [];
          const secCap = countSectionCaptured(captured, s, s.id);
          const isOpen = openSections.includes(s.id);

          return (
            <Box key={s.id} id={`dps-${s.id}`} sx={{ borderBottom: `1px solid ${INTAKE_COLORS.border}` }}>
              <Box
                onClick={() => toggleSection(s.id)}
                sx={{
                  py: 1.125,
                  cursor: "pointer",
                  bgcolor: isOpen ? INTAKE_COLORS.g1 : "transparent",
                  "&:hover": { bgcolor: INTAKE_COLORS.g1 },
                }}
              >
                <Box sx={sectionRowGrid({ alignItems: "center" })}>
                  <Box sx={{ ...iconCellSx, color: INTAKE_COLORS.textMuted }}>
                    {isOpen ? (
                      <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: INTAKE_COLORS.textMuted,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        mb: 0.25,
                      }}
                    >
                      {isSectionRequiredForIntake(s) ? "Required" : "Optional"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#383838",
                        lineHeight: 1.3,
                      }}
                    >
                      {s.name}
                    </Typography>
                  </Box>

                  <SectionStatusBadge cap={secCap} total={secItems.length} />
                </Box>

                {isOpen && (
                  <Box sx={sectionRowGrid({ mt: 1, alignItems: "center" })}>
                    <Box />
                    <Box sx={{ gridColumn: "2 / -1", minWidth: 0 }}>
                      <SectionProgressBar cap={secCap} total={secItems.length} />
                    </Box>
                  </Box>
                )}
              </Box>

              {isOpen && (
                <Box sx={{ bgcolor: INTAKE_COLORS.surface, pb: 0.5 }}>
                  {secItems.map((item) => {
                    const rawCapd = captured[item.id];
                    const gate0Locked = isGate0FrozenItem(item, gate0Data, captured, s.id);
                    const capd =
                      rawCapd && isStaleUnlinkedCapture(item, rawCapd, s.id)
                        ? undefined
                        : rawCapd;
                    const badge = itemSourceBadge(capd);
                    const dotColor = SOURCE_COLORS[badge] || SOURCE_COLORS.Pending;
                    const isEditing = editingItemId === item.id && !gate0Locked;

                    return (
                      <SubItemRow
                        key={item.id}
                        item={item}
                        capd={capd}
                        badge={badge}
                        dotColor={dotColor}
                        rowBg={INTAKE_COLORS.surface}
                        isEditing={isEditing}
                        editDraft={editDraft}
                        editError={editError}
                        editDisabled={editDisabled}
                        gate0Locked={gate0Locked}
                        onStartEdit={() => startEdit(item, capd, s.id)}
                        onEditDraftChange={(value) => {
                          setEditDraft(value);
                          if (editError) setEditError("");
                        }}
                        onSaveEdit={() => saveEdit(item)}
                        onCancelEdit={cancelEdit}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
